<template>
  <div class="page">
    <header class="header">
      <button class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </button>
      <h1 class="title">PLS备料</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <section class="input-section">
        <input
          ref="resourceNoInput"
          v-model="resourceNo"
          class="input"
          type="text"
          autocomplete="off"
          placeholder="请扫描发运单号"
          @input="handleInputChange"
          @keyup.enter="handleQuery"
        />
      </section>

      <div v-if="loading" class="status">加载中...</div>
      <div v-else-if="statusMsg" class="status" :class="statusType">
        {{ statusMsg }}
      </div>
      <div v-else-if="!records.length" class="status">请扫码查询</div>

      <section v-else>
        <div class="tab-bar">
          <button
            class="tab"
            :class="{ active: activeTab === 'summary' }"
            type="button"
            @click="activeTab = 'summary'"
          >
            物料库位信息
          </button>
          <button
            class="tab"
            :class="{ active: activeTab === 'raw' }"
            type="button"
            @click="activeTab = 'raw'"
          >
            P单列表
          </button>
        </div>

        <div class="filter-bar">
          <input
            v-model="materialFilter"
            class="filter-input"
            type="text"
            autocomplete="off"
            placeholder="物料编码筛选"
          />
          <button
            class="filter-clear"
            type="button"
            :disabled="!materialFilter"
            @click="materialFilter = ''"
          >
            清空
          </button>
        </div>

        <template v-if="activeTab === 'raw'">
          <div class="batch-actions">
            <label class="select-all">
              <input
                type="checkbox"
                :checked="allSelectableSelected"
                :disabled="loading || !selectableRecords.length"
                @change="toggleSelectAll"
              />
              全选
            </label>
            <button
              class="batch-btn"
              :disabled="loading || !selectedCount"
              @click="handleBatchComplete"
            >
              批量备料完成（{{ selectedCount }}）
            </button>
          </div>

          <div class="list">
            <div
              v-for="item in filteredRecords"
              :key="
                String(
                  item.id ||
                    item.wareOutId ||
                    item.wareOutNo ||
                    item.plsNo ||
                    item.resourceNo,
                )
              "
              class="section"
              :class="{
                selected: isSelected(item),
                disabled: isPrepared(item),
              }"
            >
              <div class="row">
                <div class="row-left">
                  <input
                    class="row-check"
                    type="checkbox"
                    :checked="isSelected(item)"
                    :disabled="loading || isPrepared(item)"
                    @change="toggleSelect(item)"
                  />
                  <div class="row-info">
                    <div
                      class="ware-out-no"
                      @click="handleWareOutNoClick(item)"
                    >
                      {{ item.wareOutNo || "-" }}
                    </div>
                    <div class="sub-line">PLS单号：{{ item.plsNo || "-" }}</div>
                  </div>
                </div>
                <div class="row-right">
                  <div
                    class="plan-status"
                    :class="isPrepared(item) ? 'done' : 'todo'"
                  >
                    {{ isPrepared(item) ? "已备料" : "未备料" }}
                  </div>
                  <div class="row-status">
                    {{ formatStatus(item.wareOutStatus ?? item.status) }}
                  </div>
                </div>
              </div>

              <div class="divider"></div>

              <div class="detail-grid" v-if="getFirstDetail(item)">
                <div class="ditem">
                  <div class="dk">物料编码</div>
                  <div class="dv">
                    {{ getFirstDetail(item).materialNo || "-" }}
                  </div>
                </div>
                <div class="ditem">
                  <div class="dk">数量</div>
                  <div class="dv qty">
                    {{ formatNum(getFirstDetail(item).wareOutNum) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="summary-list">
            <div v-if="!summaryRows.length" class="status">暂无汇总数据</div>
            <div v-else class="summary-cards">
              <div
                v-for="row in summaryRows"
                :key="row.key"
                class="summary-card"
              >
                <div class="summary-top">
                  <div class="summary-pallet">
                    托盘：{{ row.palletCode || "-" }}
                  </div>
                  <div class="summary-qty">
                    {{ formatNum(row.totalWareOutNum) }}
                  </div>
                </div>
                <div class="summary-material mono">
                  {{ row.materialNo || "-" }}
                </div>
                <div class="summary-desc" v-if="row.materialDesc">
                  {{ row.materialDesc }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <div class="pager" v-if="isPaged">
          <button
            class="btn"
            :disabled="loading || current <= 1"
            @click="handlePrevPage"
          >
            上一页
          </button>
          <div class="pager-info">{{ current }} / {{ totalPages }}</div>
          <button
            class="btn"
            :disabled="loading || current >= totalPages"
            @click="handleNextPage"
          >
            下一页
          </button>
        </div>
      </section>
    </main>

    <div
      v-if="showConfirmDialog"
      class="dialog-overlay"
      @click="closeConfirmDialog"
    >
      <div class="dialog-panel" @click.stop>
        <div class="dialog-header">
          <div class="dialog-title">确认备料完成</div>
        </div>
        <div class="dialog-content">
          确认要将选中的 {{ selectedCount }} 条出库单标记为备料完成吗？
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn cancel" @click="closeConfirmDialog">
            取消
          </button>
          <button
            class="dialog-btn confirm"
            :disabled="submitting"
            @click="confirmBatchComplete"
          >
            {{ submitting ? "提交中..." : "确认" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 库位信息显示 -->
    <div
      v-if="showLocationInfo"
      class="location-info-overlay"
      @click="hideLocationInfo"
    >
      <div class="location-info-panel" @click.stop>
        <div class="location-info-header">
          <div class="location-info-title">
            物料编码：{{ currentMaterialNo || "-" }}
          </div>
          <button class="location-info-close" @click="hideLocationInfo">
            ×
          </button>
        </div>
        <div class="location-info-content">
          <div v-if="locationList.length" class="location-info-head">
            <span class="location-info-name">库位</span>
            <span class="location-info-pallet">托盘码</span>
            <span class="location-info-qty">数量</span>
          </div>
          <div
            v-for="(location, index) in locationList"
            :key="index"
            class="location-info-row"
          >
            <span class="location-info-name">{{
              location.wareName || "-"
            }}</span>
            <span class="location-info-pallet">{{
              location.palletCode || "-"
            }}</span>
            <span class="location-info-qty">{{
              location.wareOutNum ?? location.qty ?? "-"
            }}</span>
          </div>
          <div v-if="!locationList.length" class="location-info-empty">
            暂无库位信息
          </div>
        </div>
        <div class="location-info-footer">
          <button
            class="location-info-btn"
            type="button"
            @click="hideLocationInfo"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getResourceAndNoPlsNo,
  updatePlanStatus,
  getWareOutId,
} from "@/api/wareOut";

export default {
  name: "PlsResourceNoScan",
  data() {
    return {
      resourceNo: "",
      parseTimer: null,

      activeTab: "summary",

      records: [],
      total: 0,
      current: 1,
      size: 10,
      isPaged: true,

      selectedKeys: {},

      statusMsg: "",
      statusType: "",
      loading: false,
      submitting: false,
      showConfirmDialog: false,
      showLocationInfo: false,
      locationList: [],
      currentMaterialNo: "",
      materialFilter: "",
    };
  },
  computed: {
    totalPages() {
      const pages = Math.ceil((this.total || 0) / (this.size || 10));
      return pages > 0 ? pages : 1;
    },
    selectedCount() {
      return Object.keys(this.selectedKeys).filter((k) => this.selectedKeys[k])
        .length;
    },
    allSelected() {
      if (!this.records.length) return false;
      return this.records.every((r) => this.selectedKeys[this.getKey(r)]);
    },
    filteredRecords() {
      const kw = String(this.materialFilter || "")
        .trim()
        .toLowerCase();
      if (!kw) return this.records;
      return (this.records || []).filter((item) => {
        const d = this.getFirstDetail(item);
        const materialNo = String(d?.materialNo || "");
        return materialNo.toLowerCase().includes(kw);
      });
    },
    selectableRecords() {
      return (this.filteredRecords || []).filter(
        (item) => !this.isPrepared(item),
      );
    },
    allSelectableSelected() {
      if (!this.selectableRecords.length) return false;
      return this.selectableRecords.every(
        (r) => this.selectedKeys[this.getKey(r)],
      );
    },
    summaryRows() {
      const kw = String(this.materialFilter || "")
        .trim()
        .toLowerCase();
      const map = new Map();
      (this.records || []).forEach((order) => {
        if (this.isPrepared(order)) return;
        const details = Array.isArray(order?.wareOutDetailList)
          ? order.wareOutDetailList
          : [];
        details.forEach((d) => {
          const materialNo = String(d?.materialNo || "").trim();
          if (kw && !materialNo.toLowerCase().includes(kw)) return;

          const records = Array.isArray(d?.recordList) ? d.recordList : [];
          if (!records.length) {
            const palletCode = String(d?.palletCode || "").trim();
            const key = `${palletCode}__${materialNo}`;
            const qty = Number(d?.wareOutNum);
            const safeQty = Number.isFinite(qty) ? qty : 0;

            if (!map.has(key)) {
              map.set(key, {
                key,
                palletCode,
                materialNo,
                materialDesc: d?.materialDesc || "",
                totalWareOutNum: safeQty,
              });
              return;
            }
            const row = map.get(key);
            row.totalWareOutNum = (Number(row.totalWareOutNum) || 0) + safeQty;
            return;
          }

          records.forEach((r) => {
            const palletCode = String(r?.palletCode || "").trim();
            const key = `${palletCode}__${materialNo}`;
            const qty = Number(r?.wareOutNum);
            const safeQty = Number.isFinite(qty) ? qty : 0;
            if (!map.has(key)) {
              map.set(key, {
                key,
                palletCode,
                materialNo,
                materialDesc: d?.materialDesc || "",
                totalWareOutNum: safeQty,
              });
              return;
            }
            const row = map.get(key);
            row.totalWareOutNum = (Number(row.totalWareOutNum) || 0) + safeQty;
          });
        });
      });
      return Array.from(map.values());
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.resourceNoInput) {
        this.$refs.resourceNoInput.focus();
      }
    });
  },
  beforeUnmount() {
    clearTimeout(this.parseTimer);
  },
  methods: {
    handleBack() {
      this.$router.go(-1);
    },

    handleInputChange() {
      this.resourceNo = (this.resourceNo || "").trim();

      if (!this.resourceNo) {
        clearTimeout(this.parseTimer);
        this.records = [];
        this.total = 0;
        this.current = 1;
        return;
      }

      clearTimeout(this.parseTimer);
      this.parseTimer = setTimeout(() => {
        this.current = 1;
        this.handleQuery();
      }, 300);
    },

    handleClear() {
      this.resourceNo = "";
      this.records = [];
      this.total = 0;
      this.current = 1;
      this.isPaged = true;
      this.selectedKeys = {};
      this.statusMsg = "";
      this.statusType = "";
      this.$nextTick(() => {
        if (this.$refs.resourceNoInput) {
          this.$refs.resourceNoInput.focus();
        }
      });
    },

    async handleQuery() {
      const val = (this.resourceNo || "").trim();
      if (!val) {
        this.showStatus("请扫描resourceNo", "error");
        return;
      }
      if (this.loading) return;

      this.loading = true;
      this.showStatus("搜索中...", "info");

      try {
        const resp = await getResourceAndNoPlsNo({
          resourceNo: val,
          wareOutStatus: "",
          current: this.current,
          size: this.size,
        });

        const data = resp && resp.data ? resp.data : {};
        const paged = data && Array.isArray(data.records);
        const list = paged ? data.records : Array.isArray(data) ? data : [];

        this.isPaged = paged;

        this.records = list;
        this.total = Number((paged ? data.total : list.length) || 0);
        this.selectedKeys = {};
        this.activeTab = "summary";

        if (this.records.length) {
          this.showStatus("", "");
        } else {
          this.showStatus("未查询到数据", "info");
        }
      } catch (e) {
        this.records = [];
        this.total = 0;
        this.showStatus(
          `查询失败: ${e && e.message ? e.message : "网络异常"}`,
          "error",
        );
      } finally {
        this.loading = false;
      }
    },

    handlePrevPage() {
      if (this.current <= 1) return;
      this.current -= 1;
      this.handleQuery();
    },

    handleNextPage() {
      if (this.current >= this.totalPages) return;
      this.current += 1;
      this.handleQuery();
    },

    showStatus(msg, type) {
      this.statusMsg = msg;
      this.statusType = type;
    },

    getFirstDetail(item) {
      if (!item) return null;
      const list = Array.isArray(item.wareOutDetailList)
        ? item.wareOutDetailList
        : [];
      return list.length ? list[0] : null;
    },

    formatNum(val) {
      if (val === undefined || val === null || val === "") return "-";
      const num = Number(val);
      if (Number.isNaN(num)) return String(val);
      return num.toFixed(3);
    },

    getKey(item) {
      return String(
        item?.id ||
          item?.wareOutId ||
          item?.wareOutNo ||
          item?.plsNo ||
          item?.resourceNo,
      );
    },

    isSelected(item) {
      return !!this.selectedKeys[this.getKey(item)];
    },

    toggleSelect(item) {
      if (this.isPrepared(item)) return;
      const key = this.getKey(item);
      this.selectedKeys = {
        ...this.selectedKeys,
        [key]: !this.selectedKeys[key],
      };
    },

    toggleSelectAll() {
      const next = !this.allSelectableSelected;
      const map = {};
      this.selectableRecords.forEach((r) => {
        map[this.getKey(r)] = next;
      });
      this.selectedKeys = map;
    },

    isPrepared(item) {
      return Number(item?.planStatus) === 1;
    },

    handleBatchComplete() {
      if (!this.selectedCount) return;
      this.showConfirmDialog = true;
    },

    closeConfirmDialog() {
      this.showConfirmDialog = false;
    },

    async confirmBatchComplete() {
      if (!this.selectedCount) return;
      if (this.submitting) return;

      this.submitting = true;
      try {
        const selectedIds = Object.keys(this.selectedKeys).filter(
          (k) => this.selectedKeys[k],
        );
        const ids = selectedIds.join(",");

        // 同步调用接口
        updatePlanStatus(ids);

        // 显示悬浮提示
        this.showStatus("备料完成", "success");

        // 清空输入框和查询结果
        this.resourceNo = "";
        this.records = [];
        this.total = 0;
        this.current = 1;
        this.selectedKeys = {};

        // 延迟返回备料页面，让用户看到提示
        setTimeout(() => {
          this.$router.push("/mobile/pls-material-preparation-mobile");
        }, 800);
      } catch (e) {
        this.showStatus(
          `批量备料完成失败: ${e && e.message ? e.message : "网络异常"}`,
          "error",
        );
      } finally {
        this.submitting = false;
        this.showConfirmDialog = false;
      }
    },

    async handleWareOutNoClick(item) {
      const wareOutId = item?.id || item?.wareOutId;
      if (!wareOutId) {
        this.showStatus("出库单ID不存在", "error");
        return;
      }

      const d = this.getFirstDetail(item);
      this.currentMaterialNo = d?.materialNo || "";

      this.loading = true;
      try {
        const resp = await getWareOutId(wareOutId);

        // 根据实际响应结构获取数据
        const data = resp?.data?.data || resp?.data || [];

        this.locationList = data || [];
        this.showLocationInfo = true;
      } catch (e) {
        this.showStatus(
          `查询库位失败: ${e && e.message ? e.message : "网络异常"}`,
          "error",
        );
      } finally {
        this.loading = false;
      }
    },

    hideLocationInfo() {
      this.showLocationInfo = false;
      this.locationList = [];
      this.currentMaterialNo = "";
    },

    formatStatus(val) {
      if (val === 1) return "待出库";
      if (val === 2) return "已完成";
      if (val === 4) return "已取消";
      if (val === 0) return "待处理";
      return val === undefined || val === null || val === ""
        ? "-"
        : String(val);
    },
  },
};
</script>

<style scoped>
:root {
  --background-color: #f6f7fb;
  --card-background: #ffffff;
  --header-background: #ffffff;
  --header-text: #111827;
  --text-primary: #111827;
  --text-secondary: #6b7280;
  --text-light: #9ca3af;
  --border-color: #ececf1;
  --primary-color: #2f6fff;
}
.page {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  padding-bottom: 74px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(64px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  border: none;
  background: transparent;
  padding: 6px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-icon {
  width: 22px;
  height: 22px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: var(--header-text);
  margin: 0;
  flex: 1;
  text-align: center;
}

.header-right {
  width: 40px;
  height: 40px;
}

.content {
  padding: 10px 12px;
  margin-top: calc(64px + env(safe-area-inset-top));
}

.input-section {
  background: var(--card-background);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 10px;
  border: 1px solid var(--border-color);
}

.input {
  width: 100%;
  height: 40px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  background: var(--card-background);
  color: var(--text-primary);
  box-sizing: border-box;
}

.input:focus {
  border-color: rgba(47, 111, 255, 0.45);
  box-shadow: 0 0 0 3px rgba(47, 111, 255, 0.12);
}

.status {
  text-align: center;
  color: var(--text-secondary);
  padding: 20px;
  font-size: 15px;
  border-radius: 16px;
  margin-bottom: 16px;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.status:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.status.info {
  color: var(--text-secondary);
  background: rgba(107, 114, 128, 0.05);
  border-color: rgba(107, 114, 128, 0.2);
}

.status.success {
  color: #047857;
  background: rgba(4, 120, 87, 0.05);
  border-color: rgba(4, 120, 87, 0.2);
}

.status.error {
  color: #b91c1c;
  background: rgba(185, 28, 28, 0.05);
  border-color: rgba(185, 28, 28, 0.2);
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  background: var(--card-background);
  border-radius: 16px;
  padding: 16px;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.filter-bar:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.filter-input {
  flex: 1;
  height: 44px;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 0 16px;
  background: var(--card-background);
  font-size: 15px;
  font-weight: 500;
  box-sizing: border-box;
  outline: none;
  transition: all 0.2s ease;
}

.filter-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 4px rgba(47, 111, 255, 0.1);
  transform: translateY(-1px);
}

.filter-clear {
  height: 44px;
  padding: 0 20px;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  background: var(--card-background);
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: pointer;
  white-space: nowrap;
}

.filter-clear:hover:not(:disabled) {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(47, 111, 255, 0.05);
  transform: translateY(-1px);
}

.filter-clear:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
}

.batch-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.batch-actions:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.select-all {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

.select-all input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.batch-btn {
  height: 42px;
  border: none;
  border-radius: 12px;
  padding: 0 16px;
  background: var(--primary-color);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(47, 111, 255, 0.2);
}

.batch-btn:hover:not(:disabled) {
  background: #1e5bff;
  box-shadow: 0 4px 8px rgba(47, 111, 255, 0.3);
  transform: translateY(-1px);
}

.batch-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
}

.section.selected {
  border-color: rgba(47, 111, 255, 0.35);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
}

.row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 14px;
  gap: 10px;
}

.row-left {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  flex: 1;
}
.row-check {
  width: 16px;
  height: 16px;
  margin-top: 3px;
  flex-shrink: 0;
}
.row-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}
.link {
  padding: 0;
  border: none;
  background: transparent;
  color: var(--primary-color);
  font-size: 15px;
  font-weight: 600;
  text-align: left;
  word-break: break-all;
  line-height: 1.2;
}
.link:disabled {
  opacity: 0.6;
}
.sub-line {
  color: var(--text-secondary);
  font-size: 11px;
  word-break: break-all;
  line-height: 1.2;
}
.row-status {
  font-size: 11px;
  color: var(--text-secondary);
  flex: 0 0 auto;
  background: #f5f5f7;
  padding: 2px 8px;
  border-radius: 10px;
}
.divider {
  height: 1px;
  background: var(--border-color);
  margin: 0 14px;
}
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 12px 14px;
}
.ditem {
  background: #fafbff;
  border: 1px solid #f0f1f6;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.ditem.full {
  grid-column: 1 / -1;
}
.dk {
  color: var(--text-secondary);
  font-size: 11px;
  margin-bottom: 4px;
  line-height: 1.2;
}
.dv {
  color: var(--text-primary);
  font-size: 12px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dv.desc {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dv.qty {
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-color);
}
.ware-out-no {
  color: var(--primary-color);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  word-break: break-all;
}
.ware-out-no:hover {
  text-decoration: underline;
}
.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
}
.btn {
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  background: var(--card-background);
  border-radius: 8px;
  font-size: 13px;
}
.btn:disabled {
  opacity: 0.45;
}
.pager-info {
  font-size: 12px;
  color: var(--text-secondary);
  min-width: 90px;
  text-align: center;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}
.dialog-panel {
  background: var(--card-background);
  border-radius: 12px;
  width: 100%;
  max-width: 280px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.dialog-header {
  padding: 14px 16px 8px;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
}
.dialog-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}
.dialog-content {
  padding: 12px 16px 16px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.4;
}
.dialog-footer {
  display: flex;
  border-top: 1px solid var(--border-color);
  padding: 8px 12px;
  gap: 8px;
}
.dialog-btn {
  flex: 1;
  height: 36px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}
.dialog-btn.cancel {
  color: var(--text-secondary);
  background: transparent;
}
.dialog-btn.confirm {
  color: #fff;
  background: var(--primary-color);
}
.dialog-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.location-info-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}
.location-info-panel {
  background: var(--card-background);
  border-radius: 12px;
  width: 100%;
  max-width: 280px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.location-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-color);
}
.location-info-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}
.location-info-close {
  background: none;
  border: none;
  font-size: 20px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
}
.location-info-close:hover {
  background: var(--border-color);
}
.location-info-content {
  max-height: 200px;
  overflow-y: auto;
  padding: 8px 0;
}
.location-info-head {
  display: grid;
  grid-template-columns: 1fr 1.1fr 0.6fr;
  gap: 8px;
  padding: 8px 16px;
  color: var(--text-secondary);
  font-size: 12px;
  border-bottom: 1px solid #f0f0f0;
}
.location-info-row {
  display: grid;
  grid-template-columns: 1fr 1.1fr 0.6fr;
  gap: 8px;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
}
.location-info-row:last-child {
  border-bottom: none;
}
.location-info-name {
  font-size: 13px;
  color: var(--text-primary);
}
.location-info-pallet {
  font-size: 13px;
  color: var(--primary-color);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.location-info-qty {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 600;
  text-align: right;
}
.location-info-empty {
  text-align: center;
  color: var(--text-secondary);
  padding: 20px;
  font-size: 13px;
}
.location-info-footer {
  padding: 10px 12px;
  border-top: 1px solid var(--border-color);
  background: #ffffff;
}
.location-info-btn {
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: var(--primary-color);
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
}

.tab-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.tab-bar:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.tab {
  flex: 1;
  height: 40px;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 15px;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: pointer;
}

.tab:hover {
  background: rgba(47, 111, 255, 0.05);
}

.tab.active {
  color: var(--primary-color);
  background: rgba(47, 111, 255, 0.15);
  box-shadow: 0 2px 4px rgba(47, 111, 255, 0.2);
  transform: translateY(-1px);
}

.row-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.plan-status {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 999px;
  font-weight: 600;
  border: 1px solid var(--border-color);
  flex-shrink: 0;
}

.plan-status.todo {
  color: #b45309;
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.25);
}

.plan-status.done {
  color: #047857;
  background: rgba(16, 185, 129, 0.12);
  border-color: rgba(16, 185, 129, 0.25);
}

.section.disabled {
  opacity: 0.6;
}

.summary-list {
  margin-top: 6px;
}

.summary-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.summary-card {
  background: var(--card-background);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  padding: 12px 14px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.summary-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: rgba(47, 111, 255, 0.3);
}

.summary-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.summary-pallet {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

.summary-qty {
  font-size: 16px;
  font-weight: 700;
  color: var(--primary-color);
}

.summary-material {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.summary-desc {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
